import { Bookmark, Close, Edit, FaceAdd, Hearing, Image as CarbonImage, TrashCan, Video, Reply, Flag, Chat, Pin, ThumbsUp, ArrowLeft } from "@carbon/icons-react";
import { ClickableAITag } from "component/aitag";
import { FlowBox, HorizBox, VertBox } from "component/basics";
import { ActionButton, BannerIconButton, BetaTag, BreadCrumb, ClickThroughTag, CTAButton, ExpandButton, FilterButton, IconButton, PhotoPile, ReactionButton, SubtleButton, Tag, TextButton } from "component/button";
import { colorKilauea1, colorSurfaceWarning, colorTextSecondary, colorTextPrimary, colorTextWarning } from "component/color";
import { ColoredBallPill } from "component/colorpill";
import { IconUpvote, IconUpvoted } from "component/icon";
import { FacePile } from "component/people";
import { DropDownSelector, PopupPanel } from "component/popup";
import { UtilityText, WebLinkTextButton } from "component/text";
import { useState } from "react";
import { Image, View } from "react-native";
import { CLICK, HOVER, component, DemoBox, demoFile, DemoSection, SpacedArray, story } from "system/demo";
import { t } from "util/stdtypes";

demoFile('component/button', {
    designUrl: 'https://www.figma.com/design/MX0AcO8d0ZlCBs4e9vkl5f/PSI-Design-System?node-id=1104-5678&t=MC9nppcf9h2iJDKP-1'
});

const onPress = () => {}

function CTAButtonDemo() {
    function onPress() {};

    return <VertBox gap={8}>
        <SpacedArray horiz>
            <CTAButton text='Primary' type='primary' onPress={onPress} />
            <CTAButton text='Secondary' type='secondary' onPress={onPress} />
            <CTAButton text='Accent' type='accent' onPress={onPress} />
            <CTAButton icon={<TrashCan style={{fill: colorTextWarning}} />} text='Delete' type='delete' onPress={onPress} />
            <CTAButton icon={<TrashCan style={{fill: colorTextWarning}} />} testID='delete' type='delete' onPress={onPress} />
            <CTAButton text='✨ Emoji' type='accent' onPress={onPress} />
            <CTAButton text='Disabled' type='primary' disabled />
        </SpacedArray>
        <CTAButton wide text='Wide' onPress={onPress} />
        <CTAButton size='compact' text='Compact' onPress={onPress} />
    </VertBox>
}

component('CTAButton', CTAButtonDemo, {}, [
    story('Hover Primary', [HOVER('Primary')])
]);

function ActionButtonDemo() {
    const onPress = () => {}
    return <VertBox gap={16}>
        <SpacedArray horiz>
            <ActionButton icon={Edit} text='Edit' onPress={onPress} />
            <ActionButton icon={Bookmark} text='Save' onPress={onPress} />
            <ActionButton icon={CarbonImage} text='Image' onPress={onPress} />
            <ActionButton icon={Hearing} text='Audio' onPress={onPress} />
            <ActionButton icon={Video} text='Video' onPress={onPress} />
            <ActionButton icon={FaceAdd} text='Emoji' onPress={onPress} />
        </SpacedArray>
        <SpacedArray horiz>
            <ActionButton icon={Close} onPress={onPress} />
        </SpacedArray>
    </VertBox>
}

component('ActionButton', ActionButtonDemo, {}, [
    story('Hover Edit', [HOVER('Edit')])
]);

component('IconButton', IconButton, {
    args: {icon: Edit, onPress: () => {}}
})

function SubtleButtonDemo() {
    const onPress = () => {}
    return <FlowBox rowGap={16} columnGap={32}>
        <SubtleButton icon={Reply} text='Reply' onPress={onPress} />                    
        <SubtleButton icon={IconUpvote} label={t('!Upvote ({count})')} formatParams={{count: 22}} onPress={onPress} />
        <SubtleButton icon={IconUpvoted} label={t('!Upvoted ({count})')} formatParams={{count: 23}} onPress={onPress} />
        <SubtleButton icon={Flag} text='Report' onPress={onPress} />
        <SubtleButton icon={Chat} label={t('!{count} {noun}')} 
            formatParams={{singular: 'comment', plural: 'comments', count: 12}} onPress={onPress} />
        <SubtleButton icon={Pin} text='Community Guidelines' onPress={onPress} />
    </FlowBox>
}

component('SubtleButton', SubtleButtonDemo, {}, [
    story('Hover Reply', [HOVER('Reply')])
]);

component('Text Button', TextButton, {
    args: {
        text: 'Cancel', underline: true, color: colorTextSecondary, onPress: () => {}
    }
}, [
    story('Hover', [HOVER('Cancel')])
])

function ExpandButtonDemo() {
    const [expanded, setExpanded] = useState(false);
    return <VertBox gap={16}>
        <ExpandButton testID='expand' label={t('{count} {noun}')} 
            summaryElement={<FacePile type='tiny' userIdList={['a','b','c','d']} />}
            formatParams={{count: 22, singular: 'reply', plural: 'replies'}} expanded={expanded} setExpanded={setExpanded} />
        <ExpandButton 
            summaryElement={<PhotoPile photoUrlList={['https://picsum.photos/100/100', 'https://picsum.photos/100/100', 'https://picsum.photos/100/100']} />}
            label={t('{count} {noun}')} 
            formatParams={{count: 22, singular: '!article', plural: '!articles'}} expanded={expanded} setExpanded={setExpanded} />
    </VertBox>
}

component('ExpandButton', ExpandButtonDemo, {}, [
    story('Expand', [CLICK('expand')]),
    story('Hover', [HOVER('expand')])
]);

function PhotoPileDemo() {
    return <VertBox>
        <PhotoPile photoUrlList={['https://picsum.photos/100/100']} />
        <PhotoPile photoUrlList={['https://picsum.photos/100/100', 'https://picsum.photos/100/100']} />
        <PhotoPile photoUrlList={['https://picsum.photos/100/100', 'https://picsum.photos/100/100', 'https://picsum.photos/100/100']} />
    </VertBox>
}

component('PhotoPileDemo', PhotoPileDemo);

function TagDemo() {
    return <DemoBox>
        <Tag text='Subtle Tag' type='subtle' textColor={colorTextPrimary}/>
        <Tag emoji='🔥' text='Emphasized Tag' type='emphasized' color={colorSurfaceWarning} />
        <Tag text='Tiny Tag' type='tiny' />
    </DemoBox>
}

component('Tag', TagDemo);

function ReactionButtonDemo() {
    const onPress = () => {}
    return <DemoBox>
        <SpacedArray horiz>
            <ReactionButton emoji='🤝🏽' text='Respect' count={0} onPress={onPress} />
            <ReactionButton emoji='🤝🏽' text='Respect' count={1} onPress={onPress} />
            <ReactionButton emoji='🤝🏽' text='Selected' count={1} selected onPress={onPress} />                
            <ReactionButton emoji='🤝🏽' text='View only' count={2} viewOnly onPress={onPress} />
            <ReactionButton emoji='🤝🏽' text='Text not label' onPress={onPress} />
            <ReactionButton icon={ThumbsUp} text='Like' count={0} onPress={onPress} />
            <ReactionButton icon={ThumbsUp} count={0} onPress={onPress} />
        </SpacedArray>
        <ReactionButton emoji='🤝🏽' text='Wide' wide count={0} onPress={onPress} /> 
    </DemoBox>
}

component('ReactionButton', ReactionButtonDemo);

const FilterButtonDemo = () => <DemoBox>
    <FilterButton emoji='🔍' text='Under Review' count={1} onPress={() => {}} />
    <FilterButton text='Selected' selected count={22} onPress={() => {{}}} />
</DemoBox>;
component('FilterButton', FilterButtonDemo);

component('WebLinkTextButton', WebLinkTextButton, {
    args: {numberOfLines: 3, url: 'https://example.com', text: 'Link to Example that has a title that is too long to fit on one line and so needs to be split acros multiple lines or it looks really bad' }
})

function BreadcrumbDemo() {
    return <DemoBox flow>
        <BreadCrumb icon={Close} iconProps={{size:32}} onPress={onPress} />
        <BreadCrumb icon={ArrowLeft} iconProps={{size:32}} onPress={onPress} />
        <BreadCrumb icon={Chat} iconProps={{size:24}} onPress={onPress} />
    </DemoBox>
}

component('Breadcrumb', BreadcrumbDemo);

const BannerIconButtonDemo = () => <DemoBox flow>
    <BannerIconButton type='close' onPress={onPress} />
    <BannerIconButton type='info' onPress={onPress} />
</DemoBox>;
component('BannerIconButton', BannerIconButtonDemo);

function DropDownSelectorDemo() {
    const [dropDownValue, setDropDownValue] = useState('recent');

    return <HorizBox><DropDownSelector text='Sort by' 
        value={dropDownValue} onChange={setDropDownValue}
        options={[
            {key: 'recent', label: t('Most recent')},
            {key: 'top', label: t('Top voted')}
        ]} /></HorizBox>
}

component('DropDownSelector', DropDownSelectorDemo);

function PopupPanelDemo() {
    const faceUri = 'https://psi.zdf.de/faces/face1.jpeg';

    return <DemoBox>
        <PopupPanel popupContent={() => <UtilityText text='Panel content'/>}>
            <UtilityText text='Open Panel'/>
        </PopupPanel>
        <PopupPanel popupContent={() => 
            <Image style={{width: 1000, height: 1000}} source={{uri: faceUri}} />
        }>
            <UtilityText text='Open Tall Panel'/>
        </PopupPanel>
    </DemoBox>
}

component('PopupPanel', PopupPanelDemo);


const ClickThroughTagDemo = () => <DemoBox>
    <ClickThroughTag text='Standard' onPress={() => {}} />
    <ClickThroughTag text='Standard With Emoji' emoji='🔥' onPress={() => {}} />
</DemoBox>;
component('ClickThroughTag', ClickThroughTagDemo);

component('ColoredBallPill', ColoredBallPill, {
    args: {label: t('!Label'), color: colorKilauea1}
});

const BetaTagDemo = () => <View><BetaTag/></View>;
component('Beta Tag', BetaTagDemo);

component('ClickableAITag', ClickableAITag);



